import { cn } from "@/lib/utils";
import { useAuth, useI18N, VenueDetailsBySlugResponse } from "common/hooks";
import AccessibilityItem from "@/components/VenueDetailBasic/AccessibilityItem";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatDateShort } from "common/utils/general";
import { getVenuePageTitle } from "common/utils";
import AnalyticsService from "@/services/analytics";
import { StandardButton } from "@/components/common/StandardButton";
import { Modal } from "react-responsive-modal";
import AddInterest from "@/components/VenueDetailBasic/Feature/AddInterest";
import ModalWithAction from "@/components/common/ModalWithAction";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import ScrollContainer from "react-indiana-drag-scroll";
import { Helmet } from "react-helmet-async";
import { BellIcon, FolderDottedIcon } from "@phosphor-icons/react";
import NotificationEmptyState from "@/components/VenueDetailBasic/NotificationEmptyState";

type AccessibilitySectionProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  fullSize?: boolean;
  initialSection?: string;
  onLogin: () => void;
  onRefresh?: () => void;
  cid?: string;
  tid?: string;
  onShowDetailView?: (initialSpaceId: string) => void;
  onNotificationClick?: () => void;
  isNotificationEnabled?: () => boolean;
};

function AccessibilitySection({
  venueDetailsResponse,
  fullSize,
  initialSection,
  onLogin,
  onRefresh,
  cid,
  tid,
  onShowDetailView,
  onNotificationClick,
  isNotificationEnabled,
}: AccessibilitySectionProps) {
  // Extract data from response (previously done in AccessibilityTab)
  const venueDetails = venueDetailsResponse.details;
  const accessibilityCategories = useMemo(
    () => venueDetailsResponse.accesibilityCategories || [],
    [venueDetailsResponse.accesibilityCategories],
  );
  const i18n = useI18N();
  const accessibilitySectionRefs = useRef(new Map());
  const isProgrammaticScroll = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter out items with user_roles.isGeneralUser === true
  const filteredAccessibilityCategories = useMemo(() => {
    return (accessibilityCategories || [])
      .map((category) => ({
        ...category,
        tags: category.tags
          .map((tag) => ({
            ...tag,
            reviewPhotos:
              tag.reviewPhotos?.filter(
                (photo) =>
                  photo.user_roles?.isGeneralUser !== true ||
                  photo.approved_for_public === true,
              ) || [],
            qtags:
              tag.qtags?.filter(
                (qtag) =>
                  qtag.user_roles?.isGeneralUser !== true ||
                  qtag.approved_for_public === true,
              ) || [],
          }))
          .filter(
            (tag) =>
              // Keep tags that have either reviewPhotos or qtags remaining after filtering
              (tag.reviewPhotos && tag.reviewPhotos.length > 0) ||
              (tag.qtags && tag.qtags.length > 0),
          ),
      }))
      .filter(
        (category) =>
          // Keep categories that have at least one tag remaining after filtering
          category.tags.length > 0,
      );
  }, [accessibilityCategories]);

  // Default to first category (no"All" tab) - initialize empty, set via useEffect
  const [selectedSection, setSelectedSection] = useState<string>("");

  const [showAddInterest, setShowAddInterest] = useState(false);
  const [isInterestModalOpen, setIsInterestModalOpen] = useState(false);
  const [isInterestJustSubmitted, setIsInterestJustSubmitted] = useState(false);
  const { user, isLoading: isUserDataLoading, isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();

  // Initialize the interest submitted state from localStorage
  useEffect(() => {
    if (!isLoggedIn) {
      try {
        const storedInterestsStr =
          localStorage.getItem("interested_venues") || "[]";
        const storedInterests = JSON.parse(storedInterestsStr);
        if (storedInterests.includes(venueDetails.business.id.toString())) {
          setIsInterestJustSubmitted(true);
        }
      } catch (e) {
        console.error("Error parsing interested venues from localStorage", e);
      }
    }
  }, [isLoggedIn, venueDetails.business.id]);

  // Effect to clear localStorage when user logs in
  useEffect(() => {
    if (isLoggedIn) {
      // Clear any stored interest submissions when user logs in
      localStorage.removeItem("interested_venues");
    }
  }, [isLoggedIn]);

  // CheckIcon if current user has already submitted an interest
  const hasUserInterestSubmitted = useMemo(() => {
    // For non-logged-in users, always return false so they can submit interest
    if (!user?.id || !venueDetails.businessInterestData || venueDetails.businessInterestData.length === 0 ||
      (venueDetails.businessInterestData[0].notify_accessibility_information === 0 && venueDetails.businessInterestData[0].notify_live_alert === 0)) {
      return false;
    }
    return venueDetails.businessInterestData.some(
      (interest) => interest?.user?.id === user?.id && (interest.notify_accessibility_information === 1 || interest.notify_live_alert === 1),
    );
  }, [user?.id, venueDetails.businessInterestData]);

  // Create sections with tag counts (no"All" tab)
  const accessibilitySectionsWithCounts = useMemo(() => {
    return (
      filteredAccessibilityCategories?.map((category) => ({
        name: category.catName,
        count: category.tags.length,
      })) || []
    );
  }, [filteredAccessibilityCategories]);

  // Scroll to section when tab is clicked
  const scrollToSection = (sectionName: string) => {
    const sectionRef = accessibilitySectionRefs.current.get(sectionName);
    if (sectionRef) {
      isProgrammaticScroll.current = true;
      sectionRef.scrollIntoView({ behavior: "smooth", block: "start" });
      // Move screen reader focus to the section and reset programmatic scroll flag
      setTimeout(() => {
        sectionRef.focus();
        isProgrammaticScroll.current = false;
      }, 1000);
    }
  };

  // Set first tab as selected by default when categories are loaded
  useEffect(() => {
    if (filteredAccessibilityCategories.length > 0 && !selectedSection) {
      setSelectedSection(filteredAccessibilityCategories[0].catName);
    }
  }, [filteredAccessibilityCategories, selectedSection]);

  // Handle cid/initialSection navigation
  useEffect(() => {
    if (
      cid &&
      filteredAccessibilityCategories &&
      filteredAccessibilityCategories.length > 0
    ) {
      const category = filteredAccessibilityCategories.find(
        (cat) => cat.catId === parseInt(cid),
      );
      if (category) {
        setSelectedSection(category.catName);
        // Scroll to the section after a short delay to ensure refs are set
        setTimeout(() => scrollToSection(category.catName), 100);
      }
    } else if (
      initialSection &&
      initialSection !== "All" &&
      filteredAccessibilityCategories.length > 0
    ) {
      const categoryExists = filteredAccessibilityCategories.some(
        (cat) => cat.catName === initialSection,
      );
      if (categoryExists) {
        setSelectedSection(initialSection);
        setTimeout(() => scrollToSection(initialSection), 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, filteredAccessibilityCategories, initialSection]);

  // Scroll spy - auto-select category chip when scrolling through sections
  useEffect(() => {
    if (filteredAccessibilityCategories.length === 0) return;

    // Find the scrollable parent container
    const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;
      
      let current = element.parentElement;
      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        
        if (overflowY === 'auto' || overflowY === 'scroll' || 
            overflowX === 'auto' || overflowX === 'scroll') {
          return current;
        }
        
        current = current.parentElement;
      }
      
      return null;
    };

    const scrollContainer = containerRef.current 
      ? findScrollableParent(containerRef.current) 
      : null;
    
    if (!scrollContainer) {
      return;
    }

    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      // Don't update during programmatic scrolls
      if (isProgrammaticScroll.current) {
        return;
      }

      // Debounce scroll updates to prevent jitter
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const navHeight = 64; // actual sticky nav height
        const viewportHeight = window.innerHeight;

        let activeSection = "";
        let lastAboveNav = "";

        for (const [catName, ref] of accessibilitySectionRefs.current) {
          if (!ref) continue;
          const rect = ref.getBoundingClientRect();

          if (rect.top < navHeight) {
            // Heading has scrolled above/behind the nav
            lastAboveNav = catName;
          } else if (rect.top < viewportHeight) {
            // Heading is visible in the viewport â€” topmost visible wins
            activeSection = catName;
            break;
          } else {
            // All remaining sections are below the viewport
            break;
          }
        }

        // No visible heading â€” we are scrolled inside a section
        if (!activeSection) {
          activeSection = lastAboveNav;
        }

        // Fallback: default to first section
        if (!activeSection) {
          const firstKey = accessibilitySectionRefs.current.keys().next().value;
          if (firstKey) activeSection = firstKey;
        }

        if (activeSection && activeSection !== selectedSection) {
          setSelectedSection(activeSection);
        }
      }, 100); // 100ms debounce
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [filteredAccessibilityCategories]);

  const handleAccessibilitySectionTracking = (section) => {
    console.log("section", section);
    AnalyticsService.trackGAEventHandler(
      "navigatedToAccessibilityCategoryInformation",
    );
    AnalyticsService.trackMx("navigatedToAccessibilityCategoryInformation", {
      VenueId: venueDetails.business.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueType: venueDetails.businessType,
      AccessibilitySection: section,
    });
  };

  const handleAddInterestClose = (isSuccess?: boolean) => {
    setShowAddInterest(false);
    console.log("isSuccess", isSuccess);
    console.log("isUserDataLoading", isUserDataLoading);
    console.log("isLoggedIn", isLoggedIn);

    // Only show auth modal if submission was successful AND user is not logged in
    if (isSuccess && !isUserDataLoading && !isLoggedIn) {
      setIsInterestJustSubmitted(true);
      setIsInterestModalOpen(true);
    } else {
      setIsInterestModalOpen(false);
    }
  };

  const handleNotificationClickWithTracking = () => {
    // Track analytics
    AnalyticsService.trackGAEventHandler("venueDetailIWantToKnowClicked");
    AnalyticsService.trackMx("venueDetailIWantToKnowClicked", {
      VenueId: venueDetails?.business?.id,
      VenueBrandId: venueDetails?.brandId,
      VenueBrandName: venueDetails?.brandName,
      VenueAddress: venueDetails?.address,
      VenueName: venueDetails?.name,
      VenueType: venueDetails?.businessType,
      IsEnabled: isNotificationEnabled(),
    });

    // Always open the modal
    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  return (
    <>
      {/* Scroll spy live announcement for screen readers */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {selectedSection ? `${selectedSection} section` : ""}
      </div>

      <Helmet>
        <title>
          {venueDetailsResponse?.details?.name
            ? getVenuePageTitle(
                {
                  name: venueDetailsResponse.details.name,
                  brandId: venueDetailsResponse.details.brandId,
                  route: venueDetailsResponse.details.route,
                  sublocality: venueDetailsResponse.details.sublocality,
                  postalTown: venueDetailsResponse.details.postalTown,
                  locality: venueDetailsResponse.details.locality,
                  cityLocation: venueDetailsResponse.details.cityLocation,
                },
                i18n.get("venuePageTitleSuffix"),
              )
            : i18n.get("venuePageTitleSuffix")}
        </title>
      </Helmet>

      <div ref={containerRef} className="flex flex-col w-full">
        {/* First row - Last Updated info - Mobile Only */}
        {venueDetails?.latestReviewDateUpdated && (
          <div className="py-3 md:hidden">
            <div className="flex items-center">
              <span
                className="text-fg-neutral-strong mr-2 label-m-medium"
                aria-hidden="true"
              >
                {i18n.get("lastUpdated")}:
              </span>
              <span
                className="text-fg-neutral-strong label-m-medium"
                aria-hidden="true"
              >
                {formatDateShort(venueDetails.latestReviewDateUpdated)}
              </span>
            </div>
          </div>
        )}

        {/* Category Navigation Tabs */}
        {accessibilitySectionsWithCounts.length > 0 && (
          <nav
            className="sticky top-0 z-40 bg-bg-brand-secondary-default -mx-4 px-4 md:mx-0 md:px-0 md:-mt-4 md:pt-4"
            aria-label="Accessibility categories"
          >
            {/* Category tabs */}
            <div role="tablist" aria-orientation="horizontal">
            <ScrollContainer className="flex gap-1 items-center pb-4 overflow-x-auto">
              {accessibilitySectionsWithCounts.map((section) => (
                <button
                  key={section.name}
                  type="button"
                  role="tab"
                  className={cn(
                    "flex-shrink-0 min-w-[44px] min-h-[20px] px-3 py-2 rounded-lg whitespace-nowrap transition-colors",
                    selectedSection === section.name
                      ? "label-m-semibold bg-bg-brand-tertiary-default border border-border-token-brand-subtle text-fg-brand-subtle"
                      : "label-m-medium bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover text-fg-neutral-subtle",
                  )}
                  onClick={() => {
                    handleAccessibilitySectionTracking(section.name);
                    setSelectedSection(section.name);
                    scrollToSection(section.name);
                  }}
                  aria-selected={selectedSection === section.name}
                >
                  {section.name} ({section.count})
                </button>
              ))}
            </ScrollContainer>
            </div>
          </nav>
        )}

        {/* Content Area */}
        <div className={`flex flex-col ${filteredAccessibilityCategories.length > 0 ? "pb-[100vh]" : ""} w-full min-w-0 min-h-[calc(100vh-200px)]`}>
          {/* First row - Last Updated info - Desktop Only */}
          {venueDetails?.latestReviewDateUpdated && (
            <div className="hidden md:block">
              <div className="flex items-center">
                <span
                  className="text-fg-neutral-strong mr-2 label-m-medium"
                  aria-hidden="true"
                >
                  {i18n.get("lastUpdated")}:
                </span>
                <span
                  className="text-fg-neutral-strong label-m-medium"
                  aria-hidden="true"
                >
                  {formatDateShort(venueDetails.latestReviewDateUpdated)}
                </span>
              </div>
            </div>
          )}

          {/* Accessibility Category & Tags - Always show all categories */}
          {filteredAccessibilityCategories?.map((category) => (
            <div
              key={category.catName}
              ref={(ref) => {
                if (ref) {
                  accessibilitySectionRefs.current.set(
                    category.catName,
                    ref,
                  );
                }
              }}
              className="scroll-mt-16"
              tabIndex={-1}
            >
              <AccessibilityItem
                venueId={venueDetails.id}
                businessId={venueDetails.business.id.toString()}
                category={category}
                fullSize={fullSize}
                venueName={venueDetails.name}
                onLogin={() => {
                  onLogin();
                }}
                venueDetails={venueDetails}
                categories={filteredAccessibilityCategories}
                lastUpdated={formatDateShort(
                  venueDetails.latestReviewDateUpdated,
                )}
                cid={cid}
                tid={tid}
                onShowDetailView={onShowDetailView}
              />
            </div>
          ))}

          

          {/* Empty State - No data, not subscribed */}
          {filteredAccessibilityCategories.length === 0 && !isNotificationEnabled() && (
            <div className="flex flex-1 flex-col justify-start sm:justify-center items-center px-4 pt-8 sm:pt-0">
              <FolderDottedIcon size={32} className="text-fg-neutral-strong" />
              <p className="text-center title-s-semibold text-fg-neutral-strong mt-3">
                {i18n.get("noAccessibilityInformationAvailable")}
              </p>
              <p className="text-center label-s-regular text-fg-neutral-subtle mt-2 max-w-sm">
                {i18n.get("tapIWantToKnowToGetNotifiedWhenAccessibilityInformationOrAlertsAreAdded")}
              </p>
              <StandardButton
                variant="venueNew"
                onClick={handleNotificationClickWithTracking}
                className="w-full sm:w-auto flex-shrink-0 mt-4"
              >
                <BellIcon size={20} className="text-fg-neutral-strong" />
                <span className="label-m-medium">{i18n.get("ImInterested")}</span>
              </StandardButton>
            </div>
          )}

          {/* Empty State - Subscribed/Interested */}
          {filteredAccessibilityCategories.length === 0 && isNotificationEnabled() && (
              <div className="flex flex-1 flex-col justify-start sm:justify-center items-center pt-8 sm:pt-0">
                <NotificationEmptyState
                  onManageNotifications={handleNotificationClickWithTracking}
                />
              </div>
            )}
        </div>
      </div>

      {showAddInterest && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={showAddInterest}
          onClose={() => setShowAddInterest(false)}
          aria-label="Set accessibility notifications"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <AddInterest
            venueDetailsResponse={{ details: venueDetails } as any}
            onClose={handleAddInterestClose}
            onInfoClick={() => {}}
            onRefresh={onRefresh}
          />
        </Modal>
      )}

      <ModalWithAction
        isModalOpen={isInterestModalOpen}
        title={i18n.get("thanksForYourFeedback")}
        subtitle={i18n.get("interestThankyouMessageWithCreateAccount")}
        cancelButtonTitle={i18n.get("later")}
        actionButtonTitle={i18n.get("continue")}
        onClose={() => {
          // Track skipped create account for interest
          AnalyticsService.trackGAEventHandler("skippedCreateAccount");
          AnalyticsService.trackMx("skippedCreateAccount", {
            VenueId: venueDetails.id,
            VenueBrandId: venueDetails.brandId,
            VenueBrandName: venueDetails.brandName,
            VenueAddress: venueDetails.address,
            VenueName: venueDetails.name,
            VenueType: venueDetails.businessType,
          });
          setIsInterestModalOpen(false);
        }}
        onClick={() => {
          // Track continued create account for interest
          AnalyticsService.trackGAEventHandler("continuedCreateAccount");
          AnalyticsService.trackMx("continuedCreateAccount", {
            VenueId: venueDetails.id,
            VenueBrandId: venueDetails.brandId,
            VenueBrandName: venueDetails.brandName,
            VenueAddress: venueDetails.address,
            VenueName: venueDetails.name,
            VenueType: venueDetails.businessType,
          });
          setIsInterestModalOpen(false);
          showModal(AuthModalScreen.EnterEmail);
        }}
        type="success"
        actionButtonType="default"
      />
    </>
  );
}

export default AccessibilitySection;
